import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { RefreshCw, X } from 'lucide-react';
import { APP_REFRESH_EVENT } from '../../hooks/useAppRefresh';
import {
  applyPendingUpdate,
  checkForNewVersion,
  reloadToNewVersion,
  subscribeToUpdate,
} from '../../lib/versionCheck';

/**
 * Offers a waiting update (see `lib/versionCheck`), and takes it at the next
 * page change or pull to refresh, when nothing typed on the page is lost.
 * Mounted once in App, inside the router.
 */
function UpdateAvailableBanner() {
  const [pending, setPending] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const location = useLocation();
  const firstPath = useRef(location.pathname);

  useEffect(() => subscribeToUpdate(setPending), []);

  // Moving to another page leaves the old one behind anyway.
  useEffect(() => {
    if (location.pathname === firstPath.current) return;
    firstPath.current = location.pathname;
    applyPendingUpdate();
  }, [location.pathname]);

  // Pull to refresh is the phone's reload: check, and take any update.
  useEffect(() => {
    const handler = () => {
      void checkForNewVersion(true).then((waiting) => {
        if (waiting) reloadToNewVersion();
      });
    };
    window.addEventListener(APP_REFRESH_EVENT, handler);
    return () => window.removeEventListener(APP_REFRESH_EVENT, handler);
  }, []);

  if (!pending || dismissed) return null;

  return (
    <div className="fixed inset-x-4 bottom-4 z-[1100] flex justify-center pointer-events-none">
      <div
        role="status"
        className="pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-xl border border-primary-200 bg-white p-3 shadow-lg"
      >
        <RefreshCw className="h-5 w-5 flex-shrink-0 text-primary-600" />
        <p className="min-w-0 flex-1 text-sm text-neutral-700 break-words">
          A new version of FactreeTech is available.
        </p>
        <button
          type="button"
          onClick={reloadToNewVersion}
          className="flex-shrink-0 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
        >
          Reload
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          className="flex-shrink-0 rounded-lg p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default UpdateAvailableBanner;
